import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import SearchFilter from '../../components/AllCampaigns/SearchFilter';
import StatCards from '../../components/AllCampaigns/StatCards';
import CampaignsList from '../../components/AllCampaigns/CampaignsList';
import { deleteUtmManager, editUtmManager, getAllUtmList, saveUtmManager } from '../../api/utm';
import { useNotification } from '../../hooks/useNotification';
import {
    getCampaignStats,
    getFormPayload,
    getRowsFromResponse,
    getSourceOptionList,
    normalizeCampaignRow,
    validateCampaignForm,
} from './utils';

const AllCampaignPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchText, setSearchText] = useState(() => searchParams.get('search') || '');
    const [selectedStatus, setSelectedStatus] = useState(() => searchParams.get('status') || '');
    const [selectedSources, setSelectedSources] = useState(() => searchParams.get('sources')?.split(',').filter(Boolean) || []);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const { showErrorNotification, showSuccessNotification } = useNotification();

    const dateRange = useSelector((state) => state.filter.dateRange);

    const loadCampaigns = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (searchText) params.searchTerm = searchText;
            if (selectedStatus) params.status = selectedStatus;
            if (selectedSources && selectedSources.length > 0) params.sources = selectedSources.join(',');
            if (dateRange?.startDate && dateRange?.endDate) {
                params.startDate = moment(dateRange.startDate).format('YYYY-MM-DD');
                params.endDate = moment(dateRange.endDate).format('YYYY-MM-DD');
            }

            const response = await getAllUtmList(params);
            const rows = getRowsFromResponse(response).map(normalizeCampaignRow);
            setCampaigns(rows);
        } catch (error) {
            showErrorNotification(error?.response?.data?.message || error?.message || 'Failed to load campaigns');
        } finally {
            setLoading(false);
        }
    }, [dateRange, searchText, selectedSources, selectedStatus, showErrorNotification]);

    useEffect(() => {
        loadCampaigns();
    }, [loadCampaigns]);

    useEffect(() => {
        const params = new URLSearchParams(searchParams);

        if (searchText) {
            params.set('search', searchText);
        } else {
            params.delete('search');
        }

        if (selectedStatus) {
            params.set('status', selectedStatus);
        } else {
            params.delete('status');
        }

        if (selectedSources.length > 0) {
            params.set('sources', selectedSources.join(','));
        } else {
            params.delete('sources');
        }

        const nextParams = params.toString();
        if (nextParams !== searchParams.toString()) {
            setSearchParams(params, { replace: true });
        }
    }, [searchText, selectedStatus, selectedSources, searchParams, setSearchParams]);

    const filteredCampaigns = campaigns;

    const stats = useMemo(() => getCampaignStats(campaigns), [campaigns]);
    const sourceOptions = useMemo(() => getSourceOptionList(), []);
    const statusOptions = [
        { key: 'active', label: 'Active' },
        { key: 'paused', label: 'Paused' }
    ];

    const handleStatCardClick = (statusKey) => {
        setSelectedStatus((prev) => (prev === statusKey ? '' : statusKey));
    };

    const handleCreateCampaign = async (formData) => {
        const errors = validateCampaignForm(formData);
        if (Object.keys(errors).length > 0) {
            return { success: false, errors };
        }

        try {
            const payload = getFormPayload(formData);
            const savedRecord = await saveUtmManager(payload);
            showSuccessNotification('Campaign created successfully');
            await loadCampaigns();
            return {
                success: true,
                data: normalizeCampaignRow(savedRecord || payload, campaigns.length),
            };
        } catch (error) {
            return {
                success: false,
                errors: {},
                message: error?.response?.data?.message || error?.message || 'Failed to create campaign',
            };
        }
    };

    const handleEditCampaign = async (formData, selectedCampaign) => {
        const errors = validateCampaignForm(formData);
        if (Object.keys(errors).length > 0) {
            return { success: false, errors };
        }

        try {
            const payload = {
                ...getFormPayload(formData),
                id: selectedCampaign?.id,
            };
            const updatedRecord = await editUtmManager(payload);
            showSuccessNotification('Campaign updated successfully');
            await loadCampaigns();
            return {
                success: true,
                data: normalizeCampaignRow(updatedRecord || payload, 0),
            };
        } catch (error) {
            return {
                success: false,
                errors: {},
                message: error?.response?.data?.message || error?.message || 'Failed to update campaign',
            };
        }
    };

    const handleStatusUpdate = async (campaign, isActive) => {
        try {
            await editUtmManager({
                id: campaign.id,
                ...getFormPayload(campaign),
                isActive,
            });
            showSuccessNotification(`Campaign ${isActive ? 'activated' : 'paused'} successfully`);
            await loadCampaigns();
        } catch (error) {
            showErrorNotification(error?.response?.data?.message || error?.message || 'Failed to update campaign status');
        }
    };

    const handleCostUpdate = async (campaign, newCost) => {
        try {
            await editUtmManager({
                id: campaign.id,
                ...getFormPayload(campaign),
                cost: newCost,
            });
            showSuccessNotification('Campaign cost updated successfully');
            await loadCampaigns();
        } catch (error) {
            showErrorNotification(error?.response?.data?.message || error?.message || 'Failed to update campaign cost');
        }
    };

    const handleDeleteCampaign = async (campaign) => {
        try {
            await deleteUtmManager(campaign.id);
            showSuccessNotification('Campaign deleted successfully');
            await loadCampaigns();
        } catch (error) {
            showErrorNotification(error?.response?.data?.message || error?.message || 'Failed to delete campaign');
        }
    };

    return (
        <div>
            <div className="d-flex gap-16 align-items-center">
                <span>
                    <img
                        src="/assets/images/admin-panel/all-campaign.svg"
                        className="img-fluid"
                        alt=""
                    />
                </span>
                <div className="">
                    <div className="d-flex gap-10 text-black align-items-center fw-bold font-p font-36">
                        UTM Campaign
                    </div>
                </div>
            </div>

            <div>
                <StatCards
                    stats={stats}
                    loading={loading}
                    selectedStatus={selectedStatus}
                    onStatClick={handleStatCardClick}
                />
            </div>

            <SearchFilter
                onStatusChange={setSelectedStatus}
                selectedSources={selectedSources}
                onSourceChange={setSelectedSources}
                selectedStatus={selectedStatus}
                onCreateCampaign={handleCreateCampaign}
                sourceOptions={sourceOptions}
                statusOptions={statusOptions}
            />

            <div className="mt-32">
                <CampaignsList
                    selectedStatus={selectedStatus}
                    selectedSources={selectedSources}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    data={filteredCampaigns}
                    loading={loading}
                    onEditCampaign={handleEditCampaign}
                    onStatusUpdate={handleStatusUpdate}
                    onCostUpdate={handleCostUpdate}
                    onDeleteCampaign={handleDeleteCampaign}
                    sourceOptions={sourceOptions}
                />
            </div>

        </div>
    );
};

export default AllCampaignPage;
